/**
 * Console API client.
 *
 * The session token lives in sessionStorage, not localStorage: a compliance
 * console left open on a shared machine should not survive the browser being
 * closed. Every 401 drops the session and bounces to login.
 */

/** Fired when the server rejects our session, so the shell can return to sign-in. */
export const SESSION_LOST = 'wekalp:session-lost';

const TOKEN_KEY = 'wekalp.consent.token';
const USER_KEY = 'wekalp.consent.user';

export interface SessionUser {
  userId: string;
  tenantId: string;
  tenantName?: string;
  email: string;
  name: string;
  /** Role key. System keys are fixed; custom roles start with custom_. */
  role: string;
  /** screen:action permissions the role grants. Owner holds everything. */
  permissions?: string[];
}

/** A sign-in that still owes a second factor, or must enrol one first. */
export interface MfaStep { mfa: 'verify' | 'enrol'; challenge: string }

export function isMfaStep(v: SessionUser | MfaStep): v is MfaStep {
  return (v as MfaStep).mfa !== undefined;
}

type SignInReply = { token: string; user: SessionUser } | { mfa: 'verify' | 'enrol'; challenge: string };

function finishSignIn(res: SignInReply): SessionUser | MfaStep {
  if ('mfa' in res) return { mfa: res.mfa, challenge: res.challenge };
  setSession(res.token, res.user);
  return res.user;
}

/** A change held for a second person (HTTP 202 from a gated action). */
export interface Approval {
  id: string;
  action: string;
  actionLabel: string;
  summary: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'expired' | 'failed';
  requestedBy: string;
  requestedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionReason: string | null;
  expiresAt: string;
  executedAt: string | null;
  result: Record<string, unknown> | null;
  error: string | null;
  mine: boolean;
  canDecide: boolean;
}

/** True when a write came back held for approval rather than done. */
export function heldForApproval(res: unknown): res is { approval: Approval } {
  return !!res && typeof res === 'object' && 'approval' in res && !!(res as { approval?: Approval }).approval;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) {
    super(message);
  }
}

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function getUser(): SessionUser | null {
  const raw = sessionStorage.getItem(USER_KEY);
  return raw ? (JSON.parse(raw) as SessionUser) : null;
}

export function setSession(token: string, user: SessionUser): void {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
}

/**
 * No request waits forever. A deployment mid-restart, or a mail server the
 * API is stuck talking to, must surface as an error the person can act on —
 * not as a button that says "Sending…" for two minutes.
 */
const REQUEST_TIMEOUT_MS = 20_000;

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  } catch (err) {
    const timedOut = err instanceof Error && (err.name === 'TimeoutError' || err.name === 'AbortError');
    throw new ApiError(
      0,
      timedOut
        ? 'Request timed out.'
        : 'Server unreachable.',
    );
  }

  if (res.status === 401 && !path.endsWith('/auth/login') && !path.startsWith('/v1/auth/mfa/') && !path.startsWith('/v1/me/mfa/')) {
    // There is no '#/login' route — the login screen is what App renders when it
    // has no user. Navigating to a route that does not exist left the console
    // showing a stale shell where every request failed silently, so the loss is
    // announced and App drops back to the sign-in screen.
    clearSession();
    window.dispatchEvent(new CustomEvent(SESSION_LOST));
    throw new ApiError(401, 'Session expired. Sign in again.');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? JSON.parse(text) : {};
  if (!res.ok) {
    // The API names the offending fields; showing only the headline turned a
    // precise complaint into 'request body failed validation'.
    const detail = detailMessages(payload.details).join('; ');
    const headline = payload.message ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, detail ? `${headline}: ${detail}` : headline, payload.details);
  }
  return payload as T;
}

export const api = {
  delete<T = unknown>(path: string): Promise<T> { return request<T>('DELETE', path); },
  get: <T,>(path: string) => request<T>('GET', path),
  post: <T,>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T,>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T,>(path: string, body?: unknown) => request<T>('PATCH', path, body),

  /** Step 1 of passwordless sign-in: a six-digit code goes to the address. */
  requestCode(email: string) {
    return request<{ message: string; expiresInMinutes: number }>('POST', '/v1/auth/code', { email });
  },

  /** Step 2: the code becomes a session, or the two-factor step when one is owed. */
  async verifyCode(email: string, code: string): Promise<SessionUser | MfaStep> {
    return finishSignIn(await request<SignInReply>('POST', '/v1/auth/verify', { email, code }));
  },

  /** An invitation or owner-issued link (`#/signin?t=…`), redeemed once. */
  async consumeLink(token: string): Promise<SessionUser | MfaStep> {
    return finishSignIn(await request<SignInReply>('POST', '/v1/auth/link', { token }));
  },

  /** Legacy password sign-in; the console no longer offers it. */
  async login(email: string, password: string): Promise<SessionUser | MfaStep> {
    return finishSignIn(await request<SignInReply>('POST', '/v1/auth/login', { email, password }));
  },

  /** Two-factor: the authenticator or recovery code finishes the sign-in. */
  async mfaVerify(challenge: string, code: string): Promise<SessionUser> {
    const res = await request<{ token: string; user: SessionUser }>('POST', '/v1/auth/mfa/verify', { challenge, code });
    setSession(res.token, res.user);
    return res.user;
  },

  /** Enrolment the organisation requires before the first session. */
  mfaEnrolStart(challenge: string) {
    return request<{ secret: string; otpauthUri: string }>('POST', '/v1/auth/mfa/enrol/start', { challenge });
  },

  async mfaEnrolConfirm(challenge: string, code: string): Promise<{ user: SessionUser; recoveryCodes: string[] }> {
    const res = await request<{ token: string; user: SessionUser; recoveryCodes: string[] }>('POST', '/v1/auth/mfa/enrol/confirm', { challenge, code });
    setSession(res.token, res.user);
    return { user: res.user, recoveryCodes: res.recoveryCodes };
  },

  async logout() {
    try {
      await request('POST', '/v1/auth/logout');
    } finally {
      clearSession();
    }
  },

  /** Downloads go through fetch so the Authorization header travels with them. */
  async download(path: string, filename: string) {
    const res = await fetch(path, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new ApiError(res.status, `Download failed (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};

/* ------------------------------------------------------------- banner */

/**
 * How a cookie banner looks and reads. Mirrors the API contract in
 * packages/api/src/modules/collection-points/banner-appearance.ts by hand; the
 * console does not import from the API package.
 */
export interface BannerText {
  title?: string;
  body?: string;
  accept?: string;
  reject?: string;
  customise?: string;
  noticeLink?: string;
  reopen?: string;
}

export interface BannerAppearance {
  layout: {
    /** card: the panel. bar: one strip across the page. wall: centred, page dimmed. */
    shape: 'card' | 'bar' | 'wall';
    /** Ignored for wall. */
    position: 'bottom_left' | 'bottom_right' | 'bottom' | 'top';
    rail: boolean;
    languageControl: boolean;
    /** Wall only. */
    holdPage: boolean;
  };
  buttons: {
    acceptAll: boolean;
    /** Cannot be false while acceptAll is true. */
    rejectAll: boolean;
    customise: boolean;
    rejectStyle: 'reject_all' | 'essential_only';
    order: 'reject_first' | 'accept_first';
  };
  /** Per language code. Missing strings fall back to the language pack. */
  text: Record<string, BannerText>;
  look: {
    brand: string;
    button: string;
    buttonText: string;
    surface: string;
    text: string;
    border: string;
    font: 'inherit' | 'system' | 'manrope' | 'plex';
    baseSize: number;
    radius: 'square' | 'soft' | 'round';
    /** A tenant asset id, served at GET /v1/assets/:id. */
    logoAssetId: string | null;
    poweredBy: boolean;
    /** Applied inside the banner after the built-in styles. Not contrast-checked. */
    customCss: string;
  };
  /**
   * The banner's markup and style. base names a built-in design; custom means
   * html and css are the author's own. A built-in design carries html and css
   * only once the author edited it, and base records where they started.
   */
  template: {
    base: BannerTemplateBase;
    html: string;
    css: string;
    /** A short name the author gave an edited or imported template. */
    name: string;
  };
  reopen: {
    floating: 'off' | 'bottom_left' | 'bottom_right';
    icon: 'shield' | 'cookie' | 'text';
  };
}

export type BannerTemplateBase = 'card' | 'strip' | 'wall' | 'sheet' | 'toast' | 'brand' | 'custom';

/** One thing wrong with a template, from the rules (where: html or css) or measured in the preview (no where). */
export interface BannerTemplateIssue { code: string; message: string; where?: 'html' | 'css' }

/** A built-in design as GET /v1/banner-templates lists it. */
export interface BannerTemplateListing {
  key: Exclude<BannerTemplateBase, 'custom'>;
  name: string;
  description: string;
  html: string;
  css: string;
}

/**
 * GET /v1/collection-points/:id/banner and what every write returns. A point
 * never published reads as the standard look at version 0; draft is the live
 * copy when none is saved, and hasDraft says which.
 */
export interface BannerState {
  live: BannerAppearance;
  draft: BannerAppearance;
  hasDraft: boolean;
  version: number;
  hash: string;
  publishedAt: string | null;
  publishedBy: string | null;
  defaults: BannerAppearance;
}

/** The widget config the preview page renders; only the languages are read here. */
export interface BannerPreviewConfig {
  languages?: Array<{ code: string; endonym: string; dir: 'ltr' | 'rtl' }>;
  [key: string]: unknown;
}

export interface TenantAsset { id: string; url: string; size: number }

export const banner = {
  get: (pointId: string) => request<BannerState>('GET', `/v1/collection-points/${pointId}/banner`),
  saveDraft: (pointId: string, appearance: BannerAppearance) =>
    request<BannerState>('PUT', `/v1/collection-points/${pointId}/banner`, { appearance }),
  publish: (pointId: string) => request<BannerState>('POST', `/v1/collection-points/${pointId}/banner/publish`),
  discardDraft: (pointId: string) => request<BannerState>('DELETE', `/v1/collection-points/${pointId}/banner/draft`),
  previewConfig: (pointId: string, lang: string, draft = true) =>
    request<BannerPreviewConfig>('GET', `/v1/collection-points/${pointId}/banner/preview-config?draft=${draft ? 1 : 0}&lang=${encodeURIComponent(lang)}`),
  uploadLogo: (contentType: 'image/svg+xml' | 'image/png', dataBase64: string) =>
    request<TenantAsset>('POST', '/v1/tenant/assets', { kind: 'logo', contentType, dataBase64 }),

  /** The six built-in designs with their markup and style. */
  templates: () => request<BannerTemplateListing[]>('GET', '/v1/banner-templates'),
  /** The server's reading of the template rules; the console runs the same rules locally first. */
  checkTemplate: (html: string, css: string) =>
    request<{ issues: BannerTemplateIssue[] }>('POST', '/v1/banner-templates/check', { html, css }),

  /** The banner as a JSON file; the filename comes from the response header. */
  async exportFile(pointId: string): Promise<string> {
    let res: Response;
    try {
      res = await fetch(`/v1/collection-points/${pointId}/banner/export`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
    } catch {
      throw new ApiError(0, 'Server unreachable.');
    }
    if (res.status === 401) {
      clearSession();
      window.dispatchEvent(new CustomEvent(SESSION_LOST));
      throw new ApiError(401, 'Session expired. Sign in again.');
    }
    if (!res.ok) throw new ApiError(res.status, `Export failed (${res.status})`);
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]!) : 'banner.json';
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return filename;
  },
  /** Body { document }: a file made by export. Answers the banner state, or 400 with the issues. */
  importFile: (pointId: string, document: unknown) =>
    request<BannerState>('POST', `/v1/collection-points/${pointId}/banner/import`, { document }),
};

/**
 * The messages inside an API refusal, whatever shape the details take:
 * template issues ({ code, message }), zod issues ({ path, message }) or a
 * list of strings.
 */
export function detailMessages(details: unknown): string[] {
  if (!Array.isArray(details)) return [];
  return details.map((d) => {
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object') {
      const r = d as Record<string, unknown>;
      const message = String(r.message ?? r.problem ?? '');
      const path = r.path ?? r.field;
      return path && !r.code ? `${String(path)}: ${message}` : message;
    }
    return String(d);
  }).filter(Boolean);
}

/** True when the session's role grants the screen action. Owner holds everything. */
export function can(user: SessionUser | null, permission: string): boolean {
  if (!user) return false;
  if (user.role === 'owner') return true;
  return (user.permissions ?? []).includes(permission);
}

/** Every screen has a view permission; a page is reachable when the role has it. */
export function canView(user: SessionUser | null, screen: string): boolean {
  return can(user, `${screen}:view`);
}

const SYSTEM_ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  compliance_admin: 'Compliance administrator',
  ops_user: 'Operations',
  it_admin: 'IT administrator',
  partner_readonly: 'Partner (read only)',
};

/** Display name for a role key when the roles list is not to hand. */
export function roleLabel(key: string, roles?: Array<{ key: string; name: string }>): string {
  const found = roles?.find((r) => r.key === key);
  if (found) return found.name;
  return SYSTEM_ROLE_LABELS[key] ?? key.replace(/^custom_/, '').replace(/_[a-z0-9]{6}$/, '').replace(/_/g, ' ');
}
